import React from "react";
import styled, { keyframes } from "styled-components";
import { ArrowDown01Icon } from "@hugeicons/core-free-icons";
import HugeIcon from "src/components/HugeIcon";
import { colors } from "src/theme/colors";
import { withAlpha } from "src/theme/ui";

/**
 * A box of fixed height that scrolls, and says so without a scrollbar.
 *
 * For a list whose length is the backend's business. A composition of four
 * holdings and one of fifteen cannot both set the height of the card they
 * sit in — the card is beside a chart, and letting the list decide made a
 * fifteen-holding smartfolio twice as tall as the chart next to it.
 *
 * The bar is hidden at rest and appears while the pane is being used, which
 * is what an overlay scrollbar does on a Mac and what a permanent 6px track
 * down the inside of a card should never do. But a hidden bar cannot say
 * there is more to see, so the edges do: a soft fade at whichever end has
 * content beyond it, gone at the top when you are at the top and gone at the
 * bottom when you reach the end. A fade is a better signal than a bar
 * anyway — it is ON the content it is talking about.
 */
const Frame = styled.div<{ $height?: number; $max?: number }>`
  position: relative;
  min-width: 0;

  /**
   * The fill has to be declared HERE too, not only on the pane inside.
   * The frame is what the caller's flex column actually lays out, so left
   * at its default it took its content's full height and the pane's own
   * grow rule had nothing to shrink against — the list ran straight out of
   * the bottom of its card.
   *
   * Not in cap mode: there the frame is SUPPOSED to be as tall as its
   * content, up to the cap. But it still takes the WIDTH it is given, which
   * had been arriving from that grow rule by accident: a capped frame
   * inside a flex ROW — which is what the ring's legend box is — has
   * neither a basis nor a grow rule of its own, so it shrank to the widest
   * row it held. A three-sector legend came out 185px wide in a 325px card,
   * the shares stopping halfway across with a hand's breadth of paper to
   * the right of them. How wide the box is, is the caller's business; the
   * frame's business is to take it.
   */
  ${({ $height, $max }) =>
    $height === undefined && $max === undefined
      ? "flex: 1; min-height: 0;"
      : "width: 100%;"}
`;

const Pane = styled.div<{ $height?: number; $max?: number }>`
  /* Three modes. A number is a fixed height; a cap is "as tall as the
     content until it reaches this, then scroll"; nothing is "fill whatever
     the parent gives you", for a pane that shares a bounded card with a
     footer.

     The cap is what a LIST inside a card that is already the right height
     wants: a fixed pane centres three rows in the middle of a tall box and
     leaves a band of paper above and below them, which is how a legend of
     three sectors ended up floating in the middle of its card. */
  ${({ $height, $max }) =>
    $height !== undefined
      ? `height: ${$height}px;`
      : $max !== undefined
      ? `max-height: ${$max}px;`
      : "position: absolute; inset: 0;"}
  overflow-y: auto;
  /* Room at the end of the list for the cue to sit in. Without it the disc
     landed on top of whichever row happened to be last in view and covered
     its name — a control obscuring the content it is there to reveal. */
  padding-bottom: 22px;
  overscroll-behavior: contain;

  /* Firefox: a thin track, coloured only when in use. */
  scrollbar-width: thin;
  scrollbar-color: transparent transparent;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    border-radius: 3px;
    background: transparent;
    transition: background 0.2s ease;
  }

  &:hover,
  &[data-active="true"] {
    scrollbar-color: ${colors.gray[300]} transparent;
  }

  &:hover::-webkit-scrollbar-thumb,
  &[data-active="true"]::-webkit-scrollbar-thumb {
    background: ${colors.gray[300]};
  }
`;

/* Over the content, so it reads as the content fading rather than as a
   band drawn across it — and it must not take the pointer, or the top of
   the list would not be clickable. */
const Fade = styled.div<{ $edge: "top" | "bottom"; $on: boolean }>`
  position: absolute;
  left: 0;
  right: 6px;
  ${({ $edge }) => ($edge === "top" ? "top: 0;" : "bottom: 0;")}
  height: 26px;
  pointer-events: none;
  opacity: ${({ $on }) => ($on ? 1 : 0)};
  transition: opacity 0.2s ease;
  background: linear-gradient(
    ${({ $edge }) => ($edge === "top" ? "to bottom" : "to top")},
    ${colors.white} 0%,
    ${withAlpha(colors.white, 0.72)} 45%,
    ${withAlpha(colors.white, 0)} 100%
  );
`;

/**
 * The cue: there is more below, and this takes you there.
 *
 * The fade alone was too quiet. It says something is being cut off, but only
 * to a reader who is already looking at the bottom edge — and a list that
 * ends in a soft edge inside an already-soft card reads as a design flourish
 * rather than as a fact about the content. Measured on the exposure card, a
 * pane with five more bands below the fold looked finished.
 *
 * So: a mark that MOVES. Motion is the one channel nothing else on these
 * cards is using, which is why it is legible at this size — a 24px disc
 * nudging down and settling is seen in peripheral vision, where a gradient is
 * not.
 *
 * It is a real button, not a decoration: it looks pressable, so it is, and it
 * pages the list down by most of a screenful — the same distance the scroll
 * bar's own trough click would travel. Nothing that looks like a control in
 * this product is allowed to be inert.
 *
 * It appears only at the bottom, never at the top: at the top of a list there
 * is nothing a reader needs telling, because the beginning is where they
 * already are. And it retires as soon as the end is reached.
 */
const bob = keyframes`
  0%, 55%, 100% {
    transform: translateY(0);
  }
  25% {
    transform: translateY(4px);
  }
`;

const More = styled.button<{ $on: boolean }>`
  position: absolute;
  left: 50%;
  bottom: 4px;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  margin-left: -14px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  /* The accent, filled. A white disc with a grey glyph was the same value as
     the card it sits on and the same value as the fade behind it, so at 24px
     it disappeared into both — which is what it was reported as doing. Filled
     in the one colour on the card that means "this is yours to press", it is
     found without being looked for, and it is still only 28px. */
  background: ${colors.primary[500]};
  color: ${colors.white};
  box-shadow: 0 2px 10px ${withAlpha(colors.primary[500], 0.4)},
    0 0 0 3px ${colors.white};
  cursor: pointer;

  opacity: ${({ $on }) => ($on ? 1 : 0)};
  visibility: ${({ $on }) => ($on ? "visible" : "hidden")};
  transition: opacity 0.2s ease, visibility 0.2s ease, background 0.15s ease;

  &:hover {
    background: ${colors.primary[600]};
  }

  > svg {
    animation: ${bob} 1.6s ease-in-out infinite;
  }

  /* The cue has done its job once the reader has taken the hint; a mark that
     keeps nodding after that is nagging, not informing. */
  &:hover > svg {
    animation: none;
  }

  @media (prefers-reduced-motion: reduce) {
    > svg {
      animation: none;
    }
  }
`;

/**
 * The pane's content, in a box the area owns.
 *
 * So there is always exactly one element whose height IS the content's
 * height, and it survives the content being swapped. Observing whatever
 * happened to be the pane's first child looked equivalent and was not: the
 * reference is captured when the observer is wired, so switching the card
 * from its ring to its list left the observer watching an element that had
 * been unmounted, and nothing measured again.
 */
const Content = styled.div`
  /**
   * Short content settles a THIRD of the way down, not at the top and not
   * in the middle.
   *
   * At the top it sat hard against the card's head with the whole surplus
   * beneath it, which reads as a list that has been cut off. Centred, a
   * two-row list floated in the middle of a tall card with nothing to
   * anchor it to the heading above. A third of the way down is the
   * compromise a person actually asks for: clearly near the top, with air
   * above it.
   *
   * Two weightless spacers do it, one either side, at one and two — so the
   * surplus is split 1:2 whatever the surplus is. Both have a zero basis
   * and can shrink, so a list TALLER than the pane pushes them to nothing and
   * starts at its own top, which is what it has to do: centring a scrolling
   * list puts its first row half out of view.
   */
  display: flex;
  flex-direction: column;
  min-height: 100%;
  min-width: 0;

  &::before,
  &::after {
    content: "";
    flex: 1 1 0;
    min-height: 0;
  }

  &::after {
    flex-grow: 2;
  }
`;

type Props = {
  /**
   * The pane's height.
   *
   * Fixed, because that is the point — a composition of three holdings and
   * one of fifteen must not set the height of the card they sit in. Omit it
   * only inside a parent that is ALREADY bounded and is a flex column, where
   * the pane takes the space left over after that parent's other rows.
   */
  height?: number;
  /**
   * A ceiling instead of a height.
   *
   * The pane is as tall as its content until it reaches this, and scrolls
   * after that — for a list inside a container whose height is already
   * settled, where a fixed pane would only pad short content out.
   */
  maxHeight?: number;
  children?: React.ReactNode;
};

const ScrollArea: React.FC<Props> = ({ height, maxHeight, children }) => {
  const paneRef = React.useRef<HTMLDivElement>(null);
  const contentRef = React.useRef<HTMLDivElement>(null);
  const [edges, setEdges] = React.useState({ top: false, bottom: false });
  const [active, setActive] = React.useState(false);
  const idle = React.useRef<number>();

  const measure = React.useCallback(() => {
    const el = paneRef.current;
    if (!el) return;
    const room = el.scrollHeight - el.clientHeight;
    const top = el.scrollTop > 2;
    /* A couple of pixels of slack: a fractional scrollHeight otherwise
       leaves the bottom fade showing at the end of the list forever. */
    const bottom = room > 2 && el.scrollTop < room - 2;
    /* Only when it has actually changed. A fresh object every time
       re-renders unconditionally, and with the observer below wired to the
       children that re-render re-ran the effect, which measured again — a
       loop that spun every frame and reset the pane's own state as it
       went. */
    setEdges((was) =>
      was.top === top && was.bottom === bottom ? was : { top, bottom },
    );
  }, []);

  /**
   * Re-measured when the content changes, not only when it is scrolled: the
   * list arrives after the first render and a filter can shorten it.
   *
   * Wired ONCE. Depending on `children` looked right and was not — children
   * is a fresh element on every render, so the effect tore the observer down
   * and rebuilt it constantly. Watching the pane and its content box covers
   * both a taller list and a shorter pane without needing to be told when
   * either changed.
   */
  React.useEffect(() => {
    const el = paneRef.current;
    const box = contentRef.current;
    if (!el || !box) return undefined;
    measure();
    const observer = new ResizeObserver(measure);
    /* The pane for a window resize, the content box for a list that has
       arrived, lengthened, or been swapped for another. */
    observer.observe(el);
    observer.observe(box);
    return () => observer.disconnect();
  }, [measure]);

  /**
   * And once more whenever the content is replaced.
   *
   * The observer catches a box that CHANGES size; it does not reliably
   * catch one whose contents are swapped for something taller in the same
   * commit, and it misses a chart that lays itself out a frame after mount.
   * Measuring again on the children is cheap and cannot loop, because
   * `measure` only sets state when the answer has actually changed.
   *
   * The extra frame is for the chart: on the tick the swap commits, its
   * canvas has not been sized yet.
   */
  React.useEffect(() => {
    measure();
    const frame = requestAnimationFrame(measure);
    return () => cancelAnimationFrame(frame);
  }, [measure, children]);

  /**
   * A native listener, not the library's synthetic one.
   *
   * `scroll` does not bubble, so React routes it through a capture-phase
   * listener on the root — and it did not reach this pane: measured, the
   * handler never ran and the fades stayed as whatever the last resize had
   * left them. Attached directly there is nothing in between.
   */
  React.useEffect(() => {
    const el = paneRef.current;
    if (!el) return undefined;
    const onScroll = () => {
      measure();
      setActive(true);
      window.clearTimeout(idle.current);
      idle.current = window.setTimeout(() => setActive(false), 900);
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      el.removeEventListener("scroll", onScroll);
      window.clearTimeout(idle.current);
    };
  }, [measure]);

  /* Most of a screenful, not all of it: a row or two carried over is what
     tells the reader the list did not jump somewhere else. */
  const pageDown = () => {
    const el = paneRef.current;
    if (!el) return;
    el.scrollBy({ top: el.clientHeight * 0.8, behavior: "smooth" });
  };

  return (
    <Frame $height={height} $max={maxHeight}>
      <Pane
        ref={paneRef}
        $height={height}
        $max={maxHeight}
        data-active={active}
        /* Reported on the element so what the component measured can be
           read from outside it, rather than inferred from an opacity. */
        data-at-top={!edges.top}
        data-at-bottom={!edges.bottom}
      >
        <Content ref={contentRef}>{children}</Content>
      </Pane>
      <Fade $edge="top" $on={edges.top} />
      <Fade $edge="bottom" $on={edges.bottom} />
      <More
        type="button"
        $on={edges.bottom}
        tabIndex={edges.bottom ? 0 : -1}
        aria-hidden={!edges.bottom}
        aria-label="Show more"
        onClick={pageDown}
      >
        <HugeIcon icon={ArrowDown01Icon} size={16} strokeWidth={2.4} />
      </More>
    </Frame>
  );
};

export default ScrollArea;
